import classNames from 'classnames'
import { useCallback, useId } from 'react'
import { ControlFieldAlign } from '../../../types'
import { useControlAriaAttributes } from '../hooks'
import styles from './ToggleSwitch.module.scss'
import type { ToggleSwitchProps } from '.'

const ToggleSwitch: React.FC<ToggleSwitchProps> = ({
  align = ControlFieldAlign.left,
  'aria-label': ariaLabel,
  'aria-labelledby': ariaLabelledBy,
  className,
  checked,
  error,
  disabled,
  label,
  onChange,
}: ToggleSwitchProps) => {
  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onChange && onChange(e.target.checked)
    },
    [onChange]
  )

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLLabelElement>) => {
      if (e.key === ' ' || e.key === 'Enter') {
        e.preventDefault()
        onChange && onChange(!checked)
      }
    },
    [onChange, checked]
  )

  const uniqueId = useId()
  const { ariaAttributes } = useControlAriaAttributes({
    ariaLabel,
    ariaLabelledBy,
    label,
    labelId: uniqueId,
  })

  return (
    <div className={classNames(styles['pintxo-toggle-switch'], className)} data-align={align}>
      <label
        className={styles['pintxo-toggle-switch__toggle']}
        data-checked={checked}
        data-disabled={disabled}
        tabIndex={0}
        data-error={error}
        onKeyDown={handleKeyDown}
      >
        <input
          aria-checked={checked}
          data-error={error}
          type="checkbox"
          disabled={disabled}
          checked={checked}
          onChange={handleChange}
          className={styles['pintxo-toggle-switch__toggle__input']}
          {...ariaAttributes}
        />
      </label>
      {label && (
        <span
          className={styles['pintxo-toggle-switch__label']}
          data-disabled={disabled}
          id={uniqueId}
        >
          {label}
        </span>
      )}
    </div>
  )
}
export default ToggleSwitch
